import type { AppManifest } from './types';
import { ieApp } from './ie';
import { notepadApp } from './notepad';
import { explorerApp } from './explorer';
import { displayApp } from './display';
import { msgboxApp } from './msgbox';
import { minesweeperApp } from './minesweeper';
import { solitaireApp } from './solitaire';
import { skifallApp } from './skifall';
import { paintApp } from './paint';
import { cmdApp } from './cmd';
import { calcApp } from './calc';
import { mediaPlayerApp } from './mediaplayer';
import { winampApp } from './winamp';
import { visualizerApp } from './visualizer';
import { outlookApp } from './outlook';
import { runApp } from './run';
import { recycleBinApp } from './recyclebin';
import { recyclePropsApp } from './recycleprops';
import { filePropsApp } from './fileprops';
import { taskmgrApp } from './taskmgr';

/**
 * The installed programs.
 *
 * ── Adding an application ───────────────────────────────────────────────────
 *   1. Create `src/apps/<id>/index.ts` exporting an `AppManifest`.
 *   2. Add it to the array below.
 * Nothing else changes: the Start menu, All Programs, file associations,
 * deep links and the taskbar all read from here. Vite gives each app its own
 * chunk automatically because manifests load components with `lazy()`.
 *
 * Order matters only for All Programs, which renders groups in first-seen
 * order and entries in this order within each group.
 */
export const apps: AppManifest[] = [
  ieApp,
  outlookApp,
  explorerApp,
  notepadApp,
  paintApp,
  cmdApp,
  calcApp,
  mediaPlayerApp,
  winampApp,
  visualizerApp,
  minesweeperApp,
  solitaireApp,
  skifallApp,
  displayApp,
  recycleBinApp,
  recyclePropsApp,
  filePropsApp,
  taskmgrApp,
  runApp,
  msgboxApp,
];

const byId = new Map(apps.map((a) => [a.id, a]));

export function getApp(id: string): AppManifest | undefined {
  return byId.get(id);
}

/** Apps that belong in Start ▸ All Programs. */
export function listApps(): AppManifest[] {
  return apps.filter((a) => a.showInAllPrograms !== false);
}

/** Apps claiming a given extension, most specific first. */
export function appsForExt(ext: string): AppManifest[] {
  const needle = ext.toLowerCase();
  return apps.filter((a) => a.openWith?.includes(needle));
}
